/* eslint-disable react/react-in-jsx-scope */
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { useAuth } from "@/auth/hooks/useAuth";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import { getDocumentCategoryInfo } from "@/documents/utils/documentTree";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { TreeRoot } from "@/types";
import { Download, FolderOpen, Info, Link } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import DocumentDetailsDialog from "./DocumentDetailsDialog";
import ImageThumbnail from "./ImageThumbnail";
import OfficeThumbnail from "./OfficeThumbnail";
import PdfThumbnail from "./PdfThumbnail";

type DocumentItem = {
  id: number;
  name: string;
  path: string;
  pp: string;
  program: string;
  project: string;
  at?: {
    date?: string;
  };
  createdAt?: string;
};

interface DocumentCardProps {
  data: DocumentItem[];
  typesDocs: Array<{
    label: string;
    value: number | string;
    children?: Array<{ label: string; value: number | string }>;
  }>;
  onDelete?: (id: number) => void | Promise<void>;
  onShowDetails?: (doc: DocumentItem) => void;
}

const DocumentCard: FC<DocumentCardProps> = ({ data, typesDocs, onDelete, onShowDetails }) => {
  const { user } = useAuth();
  const hidePostActions = useHidePostActions();
  const { postData } = usePostData<any>();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState("chronologie");
  const [hasAccessToEdit, setHasAccessToEdit] = useState(true);
  const [detailsDocument, setDetailsDocument] = useState<DocumentItem | null>(null);

  const isImage = (ext: string) =>
    ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
  const isOffices = (ext: string) =>
    ["doc", "docx", "xlsx", "ods"].includes(ext);

  const { data: treeFinancer } = useFetchDataV2<TreeRoot[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/carrier`,
    `tree-financer`,
    1000 * 60 * 60,
  );

  useEffect(() => {
    if (user) {
      setHasAccessToEdit(
        Boolean(
          user?.roles?.includes("ROLE_CARRIER") ||
          user?.roles?.includes("ROLE_CPINTERVENANT") ||
          (user?.roles?.includes("ROLE_SUBCONTRACTOR") && user?.fullAccess),
        ),
      );
    }
  }, [user]);

  const handleDelete = async (id: number) => {
    if (!id) return;

    if (onDelete) {
      await onDelete(Number(id));
      return;
    }

    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/media/delete/${id}`,
      data: {},
    });

    void res;
  };
  const handleCopyLink = async (url: string) => {
    try {
      await copyDocumentLink(url);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async (url: string, filename: string) => {
    const downloadToast = showDownloadInProgressToast({
      filename,
      kind: "file",
    });

    try {
      await downloadDocumentFile(url, filename);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename, kind: "file" });
    }
  };
  const handleShowDetails = (doc: DocumentItem) => {
    if (onShowDetails) {
      onShowDetails(doc);
      return;
    }

    setDetailsDocument(doc);
  };

  const groupedDocuments = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; date: Date; docs: DocumentItem[] }
    >();

    data?.forEach((doc) => {
      const categoryInfo = getDocumentCategoryInfo(
        typesDocs,
        doc as unknown as Record<string, unknown>,
      );
      const normalizedDoc = {
        ...doc,
        ...categoryInfo,
      };

      if (groupBy === "categorie") {
        const categoryKey = categoryInfo?.category || "Autres";
        const existing = groups.get(categoryKey);
        if (existing) {
          existing.docs.push(normalizedDoc);
        } else {
          groups.set(categoryKey, {
            key: categoryKey,
            label: categoryKey,
            date: new Date(0),
            docs: [normalizedDoc],
          });
        }
        return;
      }

      const rawDate =
        (typeof (doc as any).periodAt === "string"
          ? (doc as any).periodAt
          : (doc as any).periodAt?.date) ||
        doc.at?.date ||
        doc.createdAt;
      const parsed = rawDate ? new Date(rawDate) : null;

      if (!parsed || Number.isNaN(parsed.getTime())) {
        const undated = groups.get("non-date") || {
          key: "non-date",
          label: "non daté",
          date: new Date(0),
          docs: [],
        };
        undated.docs.push(normalizedDoc);
        groups.set("non-date", undated);
        return;
      }

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
      const existing = groups.get(key);
      if (existing) {
        existing.docs.push(normalizedDoc);
      } else {
        groups.set(key, {
          key,
          label: parsed.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          }),
          date: new Date(parsed.getFullYear(), parsed.getMonth(), 1),
          docs: [normalizedDoc],
        });
      }
    });

    return [...groups.values()].sort((a, b) =>
      groupBy === "categorie"
        ? a.label.localeCompare(b.label, "fr")
        : b.date.getTime() - a.date.getTime(),
    );
  }, [data, groupBy, typesDocs]);

  useEffect(() => {
    const groupKeys = groupedDocuments.map((group) => group.key);
    setOpenItems((prev) => {
      if (prev.length === 0) {
        return groupKeys;
      }

      const previousKeys = new Set(prev);
      const nextOpenItems = groupKeys.filter((key) => previousKeys.has(key));

      return nextOpenItems;
    });
  }, [groupedDocuments]);

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40 border-0 px-4 font-normal text-foreground/90">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="categorie">Sous-catégorie</SelectItem>
              <SelectItem value="chronologie">Chronologie</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <Accordion
        type="multiple"
        className="w-full"
        value={openItems}
        onValueChange={setOpenItems}
      >
        {groupedDocuments.map((group) => (
          <AccordionItem value={group.key} className="border-0 mb-4" key={group.key}>
            <AccordionTrigger className="py-[10px] hover:no-underline group">
              <div className="flex justify-between items-start w-full bg-[#FFFF] px-[18px] py-[10px] rounded-[9px]">
                <div className="flex items-center gap-2 flex-1 text-left pr-4">
                  <FolderOpen className="text-gray-950" />
                  <div className="flex-1 group-hover:underline text-gray-900">{group.label}</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="bg-white -mt-4 pt-8 px-4">
              <div className="flex flex-wrap mt-2 mb-4">
                {group.docs.map((pj, key) => {
                  const extension = pj.name.split(".").pop()?.toLowerCase() || "";
                  const isImg = isImage(extension);
                  const isBlobImage = pj.name?.toLowerCase() === "blob";
                  const isOffice = isOffices(extension);
                  const fileUrl = `${import.meta.env.VITE_IP_API}/media/${pj.path}`;
                  const downloadUrl = `${import.meta.env.VITE_API_ENDPOINT}/media/download/${pj.path}`;

                  return (
                    <div className="w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 p-2" key={pj.id ?? key}>
                      {isImg || isBlobImage ? (
                        <ImageThumbnail
                          url={fileUrl}
                          downloadUrl={downloadUrl}
                          treeFinancer={treeFinancer}
                          pj={pj}
                          canDelete={hasAccessToEdit && !hidePostActions}
                          onDelete={handleDelete}
                          onShowDetails={handleShowDetails}
                        />
                      ) : extension === "pdf" ? (
                        <PdfThumbnail
                          url={fileUrl}
                          downloadUrl={downloadUrl}
                          extension={extension}
                          treeFinancer={treeFinancer}
                          pj={pj}
                          canDelete={hasAccessToEdit && !hidePostActions}
                          onDelete={handleDelete}
                          onShowDetails={handleShowDetails}
                        />
                      ) : isOffice ? (
                        <OfficeThumbnail
                          srcUrl={fileUrl}
                          downloadUrl={downloadUrl}
                          extension={extension}
                          treeFinancer={treeFinancer}
                          pj={pj}
                          canDelete={hasAccessToEdit && !hidePostActions}
                          onDelete={handleDelete}
                          onShowDetails={handleShowDetails}
                        />
                      ) : (
                        <div className="rounded-md bg-muted p-3">
                          <div className="truncate text-sm font-medium text-foreground">
                            {pj.name}
                          </div>
                          <div className="mt-3 flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Voir les détails"
                              aria-label="Voir les détails"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleShowDetails(pj);
                              }}
                            >
                              <Info width={22} height={22} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Télécharger"
                              aria-label="Télécharger"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDownload(downloadUrl, pj.name);
                              }}
                            >
                              <Download width={22} height={22} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Copier le lien"
                              aria-label="Copier le lien"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCopyLink(fileUrl);
                              }}
                            >
                              <Link width={22} height={22} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <DocumentDetailsDialog
        open={Boolean(detailsDocument)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailsDocument(null);
          }
        }}
        pj={detailsDocument}
        treeFinancer={treeFinancer}
      />
    </>
  );
};

export default DocumentCard;
